import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Sticky add-to-cart bar (snippets/oudh-sticky-cart.liquid).
 *
 * Shows once the buy box's Add to bag button has scrolled above the screen; hides while that
 * button or the footer is in view. Its dropdowns drive the main variant picker rather than a form
 * of their own, and after each variant change the bar morphs itself from the section HTML that
 * `variant-picker.js` refetched, so both sets of controls always agree.
 *
 * The quantity steps the main `quantity-selector-component`, so its min, max and cart limits
 * apply, and shows whatever that selector reports back.
 *
 * @extends {Component<{ bar: HTMLElement, cta: HTMLElement, quantity: HTMLElement }>}
 */
class OudhStickyCart extends Component {
  requiredRefs = ['bar', 'cta', 'quantity'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #actionsPassed = false;
  #footerInView = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.#section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });

    const actions = this.#section?.querySelector('.oudh-buybox__actions');
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!actions) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === actions) {
          // Passed means above the screen, not merely not reached yet.
          this.#actionsPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else {
          this.#footerInView = entry.isIntersecting;
        }
      }
      this.#toggle(this.#actionsPassed && !this.#footerInView);
    });

    this.#observer.observe(actions);
    if (footer) this.#observer.observe(footer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#abortController.abort();
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #picker() {
    return this.#section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ?? null;
  }

  /**
   * Selects the same value in the main picker. A card is a radio, a long option is a select; either
   * way the change event bubbles to `variant-picker`, which does the rest.
   * @param {Event} event
   */
  select(event) {
    const source = event.target;
    const picker = this.#picker;
    if (!(source instanceof HTMLSelectElement) || !picker) return;

    const name = CSS.escape(source.dataset.optionName ?? '');
    const value = source.value;

    const radio = [...picker.querySelectorAll(`input[type="radio"][data-option-name="${name}"]`)].find(
      (input) => input instanceof HTMLInputElement && input.value === value
    );
    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const option = picker.querySelector(`option[data-option-name="${name}"]`);
    const select = option?.closest('select');
    if (select) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  increase() {
    this.#quantitySelector?.updateQuantity?.(1);
  }

  decrease() {
    this.#quantitySelector?.updateQuantity?.(-1);
  }

  get #quantitySelector() {
    return /** @type {(HTMLElement & { updateQuantity?: (step: number) => void }) | null | undefined} */ (
      this.#section?.querySelector('product-form-component quantity-selector-component')
    );
  }

  /** @param {Event} event */
  #onQuantity = (event) => {
    const { detail } = /** @type {Event & { detail?: { quantity: number, cartLine?: number } }} */ (event);
    if (!detail || detail.cartLine) return;
    this.refs.quantity.textContent = String(detail.quantity);
  };

  /** Adds through the main form, so quantity, selling plan and errors stay in one place. */
  add() {
    const button = this.#section?.querySelector('product-form-component [ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement && !button.disabled) button.click();
  }

  /** @param {boolean} visible */
  #toggle(visible) {
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    const { promise } = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);

    promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`oudh-sticky-cart[data-product-id="${this.dataset.productId}"]`);
        const bar = fresh?.querySelector('[ref="bar"]');
        const cta = fresh?.querySelector('[ref="cta"]');
        if (bar) morph(this.refs.bar, bar, { childrenOnly: true });
        if (cta) morph(this.refs.cta, cta, { childrenOnly: true });
      })
      .catch(() => {
        // Superseded or failed refetch: the main picker reports it; keep the bar as it is.
      });
  };
}

if (!customElements.get('oudh-sticky-cart')) {
  customElements.define('oudh-sticky-cart', OudhStickyCart);
}
